import React, { useEffect, useState } from "react";
import {
    List,
    ListItem,
    IconButton,
    ListItemText,
    Typography,
    Stack,
} from "@mui/material";
import KeyboardArrowRightSharpIcon from "@mui/icons-material/KeyboardArrowRightSharp";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { NUMBER } from "../../../constants/app-constants";
import CircleIcon from "@mui/icons-material/Circle";
import PageLoader from "../../common/page-loader";
import { formatDistanceToNow, isToday, isYesterday } from "date-fns";
import { prepareGADataAndSendEvent } from "../../../tracking";
import { getItem } from "../../../utils/helpers/storage-crud";
import { trackWebViewEvents } from "../../../tracking/webview-tracking";

const MyNotifications = ({
    user,
    notification,
    updateNotificationsConnect,
    fetchNotificationsConnect,
}) => {
    const { notifications, unseen } = notification || {};
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const isWebView = getItem("isWebView");
    const { access_token: accessToken } = user || {};

    const handleItemClick = (data) => {
        const { metadata } = data || {};
        const { auctionId, appointmentId, path, url } = metadata || {};

        prepareGADataAndSendEvent(
            "notificationClick",
            user,
            null,
            "Notifications page"
        );
        if (isWebView) {
            trackWebViewEvents("deepLink", {
                data: `cars24aepartner://?path=auction-details&auctionId=${auctionId}&appointmentId=${appointmentId}`,
            });
            return;
        }
        setLoading(true);
        if (path) {
            updateNotificationsConnect();
            navigate(path);
        } else {
            updateNotificationsConnect().finally(() => {
                window.location.href = url;
            });
        }
    };

    const handleMarkAllRead = () => {
        setLoading(true);
        updateNotificationsConnect()
            .then(() => {
                fetchNotifications();
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (accessToken) {
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    const fetchNotifications = () => {
        fetchNotificationsConnect({ page: NUMBER.ONE, size: NUMBER.TEN });
    };

    const renderPrimaryText = (data) => {
        const leftText = data?.metadata?.title;
        let rightText = formatDistanceToNow(new Date(data?.createdAt), {
            addSuffix: true,
        });
        if (isToday(new Date(data?.createdAt))) {
            rightText = "Today";
        }
        if (isYesterday(new Date(data?.createdAt))) {
            rightText = "Yesterday";
        }
        return (
            <Stack
                direction={"horizontal"}
                className="pr-3 mb-1"
                justifyContent={"space-between"}
                alignItems="center"
            >
                <Typography variant="subtitle1" className="font-semibold">
                    {leftText}
                </Typography>
                {data?.createdAt && (
                    <Typography variant="subtitle2" className="capitalize">
                        {rightText}
                    </Typography>
                )}
            </Stack>
        );
    };
    const renderSecondaryText = (data) => {
        return (
            <Typography variant="subtitle2" className="pr-3">
                {data?.metadata?.description}
            </Typography>
        );
    };

    return (
        <div style={{ margin: "0 -16px" }}>
            {loading && <PageLoader />}
            {isWebView && (
                <div className="h-[58px] bg-[#0059A0] mb-6 flex items-center justify-center">
                    <Typography
                        variant="h6"
                        className="w-full text-center text-white uppercase"
                    >
                        Notifications
                    </Typography>
                </div>
            )}
            <Typography
                sx={{ width: "97%" }}
                className={`text-end right-4 text-primary mb-2 ${
                    !unseen && "opacity-70 text-border pointer-events-none"
                }`}
                onClick={handleMarkAllRead}
            >
                Mark all as read
            </Typography>
            <List>
                {notifications?.map((item, idx, a) => (
                    <React.Fragment key={idx}>
                        <ListItem
                            key={idx}
                            disablePadding
                            divider={a?.length !== idx + 1}
                            sx={{
                                backgroundColor:
                                    item?.metadata?.color &&
                                    `${item?.metadata?.color}!important`,
                            }}
                            selected={!item?.seen}
                            onClick={handleItemClick.bind(null, item)}
                            className={`flex justify-between items-center flex-row pl-4 py-1 ${
                                !idx ? "" : ""
                            }`}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    aria-label="Arrow"
                                    className="-mr-7"
                                >
                                    <KeyboardArrowRightSharpIcon
                                        color="primary"
                                        className="h-8 w-8"
                                    />
                                </IconButton>
                            }
                        >
                            <React.Fragment key={idx}>
                                {!item?.seen && (
                                    <CircleIcon
                                        className="h-2.5 w-2.5 -ml-2 mr-2"
                                        color="primary"
                                    />
                                )}
                                <ListItemText
                                    primary={renderPrimaryText(item)}
                                    className="pr-8"
                                    secondary={renderSecondaryText(item)}
                                />
                            </React.Fragment>
                        </ListItem>
                    </React.Fragment>
                ))}
            </List>
        </div>
    );
};

MyNotifications.propTypes = {
    notification: PropTypes.object,
    user: PropTypes.object,
    updateNotificationsConnect: PropTypes.func,
    fetchNotificationsConnect: PropTypes.func,
};

export default MyNotifications;
